import { Injectable } from '@angular/core';
import {Subject, Observable, filter, map} from "rxjs";

/**
 * Interface representing the structure of events that flow through the event bus.
 * @template T - The type of the payload data
 */
interface EventBusEvent<T extends any> {
  /** Unique identifier for the event type */
  type: string;
  /** Data associated with the event */
  payload: T;
}

@Injectable({
  providedIn: 'root'
})
export class EventBusService {

  constructor() { }

  /** Central event bus implemented as an RxJS Subject */
  private readonly eventBus = new Subject<EventBusEvent<any>>();

  /**
   * Emits an event with the specified type and payload to all subscribers.
   * @template T - The type of the payload data
   * @param type - The event type identifier
   * @param payload - The data to be transmitted with the event
   */
  emit<T>(type: string, payload: T): void {
    this.eventBus.next({ type, payload });
  }

  /**
   * Subscribes to events of a specific type.
   * @template T - The expected type of the payload data
   * @param eventType - The event type to listen for
   * @returns An Observable that emits only the payload of matching events
   */
  on$<T>(eventType: string): Observable<T> {
    return this.eventBus.asObservable().pipe(filter((event: EventBusEvent<T>) => event.type === eventType), map((event: EventBusEvent<T>) => event.payload));
  }

}
